import { CLAUDE_MODEL, CLAUDE_MODEL_SONNET, API_URL, sb } from './config'

// === Auth header helper ===
async function getAuthHeaders() {
  const { data } = await sb.auth.getSession()
  const token = data?.session?.access_token
  return token ? { 'Authorization': `Bearer ${token}` } : {}
}

// === Claude API Helper (via backend proxy, with auto-retry) ===
const MAX_RETRIES = 3
const RETRY_DELAYS = [3000, 8000, 15000] // 3s, 8s, 15s

// Strip lone surrogates that break JSON.stringify (from Instagram emojis etc.)
// On évite les lookbehinds (?<...) car les vieux moteurs Safari/iOS lèvent
// "invalid regular expression: invalid group specifier name" — bug observé sur
// 1 user (cf. ANALYSE_USERS_2026-05-04). Implémentation char-by-char qui marche
// sur tous les moteurs JS.
function sanitizeForJSON(str) {
  if (typeof str !== 'string') return str
  let result = ''
  for (let i = 0; i < str.length; i++) {
    const code = str.charCodeAt(i)
    // High surrogate : doit être suivi d'une low surrogate
    if (code >= 0xD800 && code <= 0xDBFF) {
      const next = str.charCodeAt(i + 1)
      if (next >= 0xDC00 && next <= 0xDFFF) {
        result += str[i] + str[i + 1]
        i++
      }
      continue
    }
    // Low surrogate isolée → on saute
    if (code >= 0xDC00 && code <= 0xDFFF) continue
    result += str[i]
  }
  return result
}

function sanitizeMessages(msgs) {
  return msgs.map(m => ({ ...m, content: typeof m.content === 'string' ? sanitizeForJSON(m.content) : m.content }))
}

async function _callClaude(systemPrompt, messages, maxTokens, timeoutMs, modelOverride, signal, _isRetryAfterRefresh = false) {
  const authHeaders = await getAuthHeaders()
  const r = await fetch(`${API_URL}/api/claude`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders },
    body: JSON.stringify({ model: modelOverride || CLAUDE_MODEL, max_tokens: maxTokens, system: sanitizeForJSON(systemPrompt), messages: sanitizeMessages(messages) }),
    signal,
  })
  if (r.status === 401) {
    // 2026-05-04 : avant de throw "Session expiree" on tente un refresh.
    // 12 erreurs/4 users sur l'onboarding venaient du JWT qui expire pendant
    // les 5 min de chat (token TTL 1h, mais autoRefresh peut ne pas tirer
    // si l'onglet est en arrière-plan). On retry UNE seule fois pour éviter
    // les boucles infinies si le refresh échoue aussi.
    if (!_isRetryAfterRefresh) {
      try {
        const { data, error } = await sb.auth.refreshSession()
        if (!error && data?.session?.access_token) {
          console.log('[askClaude] JWT refreshed, retrying request')
          return _callClaude(systemPrompt, messages, maxTokens, timeoutMs, modelOverride, signal, true)
        }
      } catch (refreshErr) {
        console.warn('[askClaude] Session refresh failed:', refreshErr?.message)
      }
    }
    // Refresh impossible / déjà tenté → on vraiment expiré. Dispatche un event
    // pour que le FeedbackFAB pulse en mode "tu as eu un souci ?" (cf. ANALYSE
    // USERS_2026-05-04 — récolter du feedback à chaud sur les pannes).
    try { window.dispatchEvent(new CustomEvent('merlin:user-error', { detail: { msg: 'session_expired' } })) } catch {}
    throw new Error('Session expiree — reconnecte-toi')
  }
  const d = await r.json()
  if (d.content?.[0]?.text) return d.content[0].text
  // Check if retryable
  const errType = d.error?.type || ''
  const isRetryable = errType === 'overloaded_error' || errType === 'rate_limit_error' || r.status === 429 || r.status === 529 || r.status === 503
  if (isRetryable) {
    const err = new Error(d.error?.message || 'API surchargee')
    err._retryable = true
    throw err
  }
  throw new Error(JSON.stringify(d.error || d))
}

export async function askClaude(systemPrompt, messages, maxTokens = 2000, timeoutMs = 45000, modelOverride = null) {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    let lastErr = null
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        const result = await _callClaude(systemPrompt, messages, maxTokens, timeoutMs, modelOverride, controller.signal)
        return result
      } catch (e) {
        lastErr = e
        if (e.name === 'AbortError') throw new Error("Timeout: l'analyse a pris trop de temps")
        if (!e._retryable || attempt >= MAX_RETRIES) throw e
        const delay = RETRY_DELAYS[attempt] || 5000
        console.warn(`[askClaude] Retry ${attempt + 1}/${MAX_RETRIES} dans ${delay / 1000}s  -  ${e.message}`)
        await new Promise(r => setTimeout(r, delay))
      }
    }
    throw lastErr
  } finally {
    clearTimeout(timer)
  }
}

// === Beat Classification Agent (replaces regex-based detection) ===
// Uses Haiku to classify each beat as FILMED (spoken face camera) or ADDED (visual element)
const HAIKU_MODEL = 'claude-haiku-4-5-20251001'

export async function classifyBeatsWithAI(steps, fullRefScript, videoContext = {}, userId = null) {
  if (!steps || steps.length === 0) return { beats: [], vraiHook: null, vraiHookBeat: null }

  const beatsDescription = steps.map((s, i) => {
    const b = s.beat || {}
    const parts = [`Beat ${i + 1}`]
    if (b.fonction) parts.push(`fonction: ${b.fonction}`)
    if (b.technique) parts.push(`technique: ${b.technique}`)
    if (b.texte_cle) parts.push(`texte: "${b.texte_cle.substring(0, 300)}"`)
    // Extract first 4 plans from description_visuelle for richer visual context
    if (b.description_visuelle) {
      const planMatches = b.description_visuelle.match(/Plan \d+[^]*/g)
      if (planMatches && planMatches.length > 0) {
        // Split by "Plan N" pattern and take first 4
        const planLines = b.description_visuelle.split(/(?=Plan \d+)/).filter(l => l.trim().startsWith('Plan')).slice(0, 4)
        parts.push(`visuel: "${planLines.join(' | ')}"`)
      } else {
        parts.push(`visuel: "${b.description_visuelle.substring(0, 500)}"`)
      }
    }
    if (b.visual_data) {
      try {
        const vd = typeof b.visual_data === 'string' ? JSON.parse(b.visual_data) : b.visual_data
        if (vd.description_plan) parts.push(`plan: "${vd.description_plan.substring(0, 150)}"`)
        // Also extract plans array if available
        if (vd.plans && Array.isArray(vd.plans)) {
          const planDescs = vd.plans.slice(0, 4).map(p => p.description || p.type || '').filter(Boolean)
          if (planDescs.length) parts.push(`plans_detail: "${planDescs.join(' | ')}"`)
        }
      } catch {}
    }
    return parts.join(' | ')
  }).join('\n')

  const system = `Tu es un analyste de production video. Tu dois determiner OU COMMENCE le vrai contenu original de cette video.

DEFINITIONS :
- FILMED = contenu ORIGINAL cree pour CETTE video (face camera, nouveau)
- ADDED = contenu EXTERIEUR (extrait d'un reel precedent, video virale passee, clips d'autres createurs, screenshots, stats)

METHODE OBLIGATOIRE — 2 PASSES :

PASSE 1 (cross-references) : Avant de classifier, lis TOUS les beats et cherche les REFERENCES TEMPORELLES.
Si un beat dit "J'ai poste cette video il y a X mois" ou "ce reel a fait X vues", identifie A QUEL CONTENU il fait reference.
Ce contenu (souvent montre dans un beat PRECEDENT) est un EXTRAIT d'une video passee → ADDED.

PASSE 2 (classification) : Maintenant classifie chaque beat en tenant compte des cross-references trouvees.
Le VRAI HOOK = le premier beat FILMED (contenu nouveau).`

  // Build video context block
  const ctxParts = []
  if (videoContext.titre) ctxParts.push(`Titre: ${videoContext.titre}`)
  if (videoContext.resume) ctxParts.push(`Resume: ${videoContext.resume}`)
  if (videoContext.format) ctxParts.push(`Format: ${videoContext.format}`)
  if (videoContext.arcNarratif) ctxParts.push(`Arc narratif: ${videoContext.arcNarratif}`)
  if (videoContext.hookVerbatim) ctxParts.push(`Hook audio DB: "${videoContext.hookVerbatim}"`)
  if (videoContext.hookVisuel) ctxParts.push(`Hook visuel DB: "${videoContext.hookVisuel}"`)
  const ctxBlock = ctxParts.length > 0 ? `=== CONTEXTE DE LA VIDEO ===\n${ctxParts.join('\n')}\n\n` : ''
  console.log('[classifyBeats] videoContext:', JSON.stringify(videoContext).substring(0, 300))
  console.log('[classifyBeats] ctxBlock length:', ctxBlock.length, 'starts with:', ctxBlock.substring(0, 80))

  const prompt = `${ctxBlock}=== SCRIPT COMPLET ===
${fullRefScript ? `"${fullRefScript.substring(0, 2500)}"` : '(non disponible)'}

=== BEATS (avec descriptions visuelles) ===
${beatsDescription}

=== TA MISSION ===

PASSE 1 — Lis TOUS les beats. Cherche les references temporelles :
- "J'ai poste cette video il y a...", "ce reel a fait X vues", "il y a X mois"
- Pour chaque reference trouvee : quel beat MONTRE le contenu auquel elle fait reference ?
- Exemple : beat 2 dit "J'ai poste cette video il y a 5 mois" → le contenu de cette video = ce qui est montre en beat 1 → beat 1 est un EXTRAIT = ADDED

Mets tes trouvailles dans "cross_references" dans ta reponse.

PASSE 2 — Classifie chaque beat EN TENANT COMPTE de la passe 1 :
- Si un beat a ete identifie comme extrait via cross-reference → ADDED
- Si un beat montre des clips d'autres createurs → ADDED
- Si un beat montre des stats/screenshots → ADDED
- Sinon, si le createur cree du contenu NOUVEAU pour cette video → FILMED

Le VRAI HOOK = le texte du premier beat FILMED.

Reponds UNIQUEMENT en JSON valide:
{
  "cross_references": [{"beat_source": 2, "texte_signal": "J'ai poste cette video il y a 5 mois", "beat_contenu_reference": 1, "conclusion": "beat 1 montre l'extrait de la video precedente"}],
  "beats": [{"beat": 1, "type": "filmed|added", "raison": "explication courte"}],
  "vrai_hook": "texte du premier beat FILMED",
  "vrai_hook_beat": 4
}`

  const t0 = Date.now()
  try {
    const result = await askClaude(system, [{ role: 'user', content: prompt }], 2000, 25000, HAIKU_MODEL)
    const duration = Date.now() - t0
    const jsonMatch = result.match(/\{[\s\S]*\}/)
    if (!jsonMatch) {
      console.warn('[classifyBeats] No JSON in response, falling back')
      logPipelineStep({ step: 'classify_beats', model: HAIKU_MODEL, systemPrompt: system, userPrompt: prompt, rawOutput: result, error: 'no JSON in response', durationMs: duration, userId })
      return { beats: steps.map((_, i) => ({ beat: i + 1, type: 'filmed', raison: 'fallback' })), vraiHook: null, vraiHookBeat: null }
    }
    const parsed = JSON.parse(jsonMatch[0])
    const output = { beats: parsed.beats || [], vraiHook: parsed.vrai_hook || null, vraiHookBeat: parsed.vrai_hook_beat || null }
    logPipelineStep({ step: 'classify_beats', model: HAIKU_MODEL, systemPrompt: system, userPrompt: prompt, rawOutput: result, parsedOutput: output, durationMs: duration, userId })
    return output
  } catch (e) {
    console.error('[classifyBeats] AI classification failed:', e.message)
    logPipelineStep({ step: 'classify_beats', model: HAIKU_MODEL, systemPrompt: system, userPrompt: prompt, error: e.message, durationMs: Date.now() - t0, userId })
    return { beats: steps.map((_, i) => ({ beat: i + 1, type: 'filmed', raison: 'erreur classification' })), vraiHook: null, vraiHookBeat: null }
  }
}

// === Hook Strategy Agent ===
// Decides whether to use the DB hook formula or recreate based on the real spoken hook
export async function analyzeHookStrategy({ hookVerbatim, hookFormula, vraiHook, vraiHookBeat, beatClassification, formatNom, arcNarratif, premierBeatFonction, userId = null }) {
  // If no hook data AND no vraiHook at all, let AI decide with beat info
  if (!hookVerbatim && !hookFormula && !vraiHook) {
    // Still call the AI if we have beat context — it might determine demarrage_direct
    if (!premierBeatFonction) {
      return { strategie: 'recreer', raison: 'aucune donnee hook disponible', hookReference: null, formuleAdaptee: null }
    }
  }

  // Always call the AI agent to evaluate — even if beat 1 is filmed, the hook formula
  // may describe a concept specific to the reference video (not transposable as-is)
  const beat1 = beatClassification?.beats?.find(b => b.beat === 1)
  const beat1IsAdded = beat1?.type === 'added'

  const system = `Tu es un expert en hooks de videos virales. Tu decides si une formule de hook en base de donnees est utilisable pour TRANSPOSER le hook a n'importe quel sujet, ou s'il faut recreer une formule a partir du vrai hook oral.

IMPORTANT: une formule est "adaptable" SEULEMENT si elle est GENERIQUE — c'est-a-dire qu'on peut remplacer les slots [X] par N'IMPORTE QUEL sujet et ca fonctionne.
Exemple generique: "Si [Action], alors [Consequence]" → adaptable a tout sujet.
Exemple NON generique: "C'est ta [Concept specifique] — c'est ta [Concept specifique] interdite" → si les slots decrivent un concept PROPRE a la video de reference (zone, video virale, resultat specifique), la formule n'est PAS transposable.

Il existe aussi des videos qui n'ont PAS de hook traditionnel — elles demarrent directement avec le contenu (ex: une liste, un fait, une question, un item). Si c'est le cas, reponds avec strategie "demarrage_direct".

Quand tu choisis "recreer", propose une formule generique extraite du VRAI hook oral.`

  const prompt = `=== DONNEES HOOK EN DB ===
Hook verbatim: "${hookVerbatim || '(vide)'}"
Formule syntaxique: "${hookFormula || '(vide)'}"

=== VRAI HOOK IDENTIFIE (premier texte dit face camera) ===
"${vraiHook || '(non identifie)'}" (beat ${vraiHookBeat || '?'})

=== CLASSIFICATION DES BEATS ===
${beatClassification?.beats?.map(b => `Beat ${b.beat}: ${b.type} — ${b.raison}`).join('\n') || '(non disponible)'}

=== CONTEXTE ===
Beat 1 classifie comme: ${beat1IsAdded ? 'ADDED (element visuel)' : 'FILMED'}
Beat 1 fonction: ${premierBeatFonction || '?'}
Format: ${formatNom || '?'}
Arc narratif: ${arcNarratif || '?'}

=== QUESTION ===
1. Est-ce que cette video a un VRAI hook/accroche, ou est-ce qu'elle demarre directement avec le contenu (liste, fait, demonstration) ?
2. Si pas de hook → strategie "demarrage_direct"
3. Si hook present: la formule syntaxique en DB est-elle GENERIQUE (transposable) ou SPECIFIQUE ?
4. Si specifique: recreer une formule generique a partir du vrai hook oral.
5. Si generique: la garder telle quelle.

Reponds UNIQUEMENT en JSON:
{"strategie":"adapter_formule"|"recreer"|"demarrage_direct","raison":"explication courte","formule_adaptee":"la formule generique a utiliser (existante ou recreee, ou null si demarrage_direct)","hook_reference":"le texte oral de reference"}`

  const t0 = Date.now()
  try {
    const result = await askClaude(system, [{ role: 'user', content: prompt }], 800, 15000, HAIKU_MODEL)
    const duration = Date.now() - t0
    const jsonMatch = result.match(/\{[\s\S]*\}/)
    if (!jsonMatch) {
      console.warn('[hookStrategy] No JSON, defaulting to recreer')
      logPipelineStep({ step: 'hook_strategy', model: HAIKU_MODEL, systemPrompt: system, userPrompt: prompt, rawOutput: result, error: 'no JSON in response', durationMs: duration, userId })
      return { strategie: 'recreer', raison: 'parse error', hookReference: vraiHook, formuleAdaptee: null }
    }
    const parsed = JSON.parse(jsonMatch[0])
    const output = { strategie: parsed.strategie || 'recreer', raison: parsed.raison || '', hookReference: parsed.hook_reference || vraiHook, formuleAdaptee: parsed.formule_adaptee || null }
    logPipelineStep({ step: 'hook_strategy', model: HAIKU_MODEL, systemPrompt: system, userPrompt: prompt, rawOutput: result, parsedOutput: output, durationMs: duration, userId })
    return output
  } catch (e) {
    console.error('[hookStrategy] AI analysis failed:', e.message)
    logPipelineStep({ step: 'hook_strategy', model: HAIKU_MODEL, systemPrompt: system, userPrompt: prompt, error: e.message, durationMs: Date.now() - t0, userId })
    return {
      strategie: beat1IsAdded ? 'recreer' : 'adapter_formule',
      raison: 'fallback apres erreur',
      hookReference: vraiHook || hookVerbatim,
      formuleAdaptee: beat1IsAdded ? null : hookFormula,
    }
  }
}

// === Pipeline Logging to Supabase ===
// Logs every AI call (input + output) during script generation for debugging

let _pipelineSessionId = null
export function startPipelineSession() {
  _pipelineSessionId = `ps_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`
  console.log(`[PipelineLog] New session: ${_pipelineSessionId}`)
  return _pipelineSessionId
}
export function getPipelineSessionId() { return _pipelineSessionId }

export async function logPipelineStep({
  step,
  pipeline = null,
  model = null,
  systemPrompt = null,
  userPrompt = null,
  rawOutput = null,
  parsedOutput = null,
  durationMs = null,
  error = null,
  sourceVideo = null,
  context = null,
  userId = null,
}) {
  const sessionId = _pipelineSessionId
  const inputTokensEst = Math.round(((systemPrompt || '').length + (userPrompt || '').length) / 3.5)

  const entry = {
    session_id: sessionId,
    user_id: userId || null,
    step,
    pipeline,
    source_video_id: sourceVideo?.airtable_id || sourceVideo?.id || null,
    source_video_titre: sourceVideo?.titre || null,
    source_video_format: sourceVideo?.format_nom || null,
    model,
    system_prompt: systemPrompt,
    user_prompt: userPrompt,
    input_tokens_est: inputTokensEst,
    raw_output: rawOutput,
    parsed_output: parsedOutput || null,
    duration_ms: durationMs,
    error: error || null,
    context: context || null,
  }

  // Console log summary
  console.log(`[PipelineLog] ${step} | ${model || '?'} | ~${inputTokensEst} tokens | ${durationMs ? durationMs + 'ms' : '?'}${error ? ' | ERROR: ' + error : ''}`)

  // Async write to Supabase (fire-and-forget, never blocks pipeline)
  try {
    sb.from('pipeline_logs').insert(entry).then(({ error: dbErr }) => {
      if (dbErr) console.warn('[PipelineLog] Supabase write failed:', dbErr.message)
    })
  } catch (e) {
    console.warn('[PipelineLog] Supabase write error:', e.message)
  }

  return entry
}

// === String helpers ===
export const parseJsonArray = (v) => {
  if (!v) return []
  if (Array.isArray(v)) return v
  try { return JSON.parse(v) } catch { return [v] }
}

export const uniqueNE = (arr) => [...new Set(arr.filter(Boolean))]

export const cap = (s) => (!s || typeof s !== 'string') ? s || '' : s.charAt(0).toUpperCase() + s.slice(1)

export const trunc = (s, n) => !s ? '' : s.length > n ? s.substring(0, n) + '...' : s

export const simplify = (val) => {
  if (!val) return ''
  if (typeof val !== 'string') val = String(val)
  let s = val.replace(/_/g, ' ').replace(/"/g, '').trim()
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
}

export const formatViews = (n) => {
  if (!n) return ''
  if (n >= 1000000) return (n / 1000000).toFixed(1).replace('.0', '') + 'M'
  if (n >= 1000) return Math.round(n / 1000) + 'K'
  return String(n)
}

export function removeLanguageTics(text) {
  if (!text) return text;
  let s = text;

  // Direct word/phrase replacements  -  remove the tic word and clean up
  const tics = [
    // "mine d'or" / "mine d'or" → remove
    /\b(une |la |ta |cette |c'est une |c'est la )?mine d['']or\b/gi,
    // "pépite" / "pepite" → remove
    /\b(une |la |ta |cette |des |les )?p[eé]pites?\b/gi,
    // "machine à [X]" → remove the metaphor
    /\b(une |la |ta |cette )?machine [àa] \w+/gi,
    // "stoppe net" → remove
    /stoppe? net/gi,
    // "qui te fait arrêter ton pouce" / thumb-stopping
    /qui (?:te |vous )?fait arr[eê]ter (?:ton|votre) pouce/gi,
    // "squelette" when used metaphorically (e.g. "squelette de vidéo/script/contenu")
    /\b(un |le |ce |du )?squelette (de |du |d[''])\w+/gi,
    // "démonter" when metaphorical (e.g. "pour la démonter", "on va démonter")
    /pour (?:la |le |les )?d[eé]monter/gi,
  ];

  for (const pattern of tics) {
    s = s.replace(pattern, '');
  }

  // Clean up: double spaces, orphaned punctuation
  s = s.replace(/  +/g, ' ');           // collapse multiple spaces
  s = s.replace(/ ([.,!?])/g, '$1');     // space before punctuation
  s = s.replace(/([.,!?])\1+/g, '$1');   // repeated punctuation
  s = s.replace(/^\s+/gm, '');           // leading whitespace on lines
  s = s.replace(/\n\n\n+/g, '\n\n');     // too many blank lines

  return s.trim();
}

// === Post-generation language cleanup via LLM ===
// Detects anglicisms, copywriting patterns, unnatural phrasing
// and reformulates them naturally. Uses Haiku for speed (~1-2s).
export async function cleanupLanguageWithLLM(scriptText, formatName = '', context = {}) {
  if (!scriptText || scriptText.length < 50) return scriptText;
  // Skip for structured formats — their layout is the format
  const STRUCTURED_KEYWORDS = /classement|ranking|quiz|binaire|tier.?list|versus|comparati[fv]|sondage|vote|choix binaire/i;
  if (formatName && STRUCTURED_KEYWORDS.test(formatName)) {
    console.log('[reviewScript] Skipped: structured format "' + formatName.substring(0, 40) + '"');
    return scriptText;
  }
  try {
    // Build context block for Sonnet
    const ctxLines = [];
    if (context.refScript) ctxLines.push(`SCRIPT DE REFERENCE (la source):\n"${context.refScript.substring(0, 800)}"`);
    if (context.creatorName) ctxLines.push(`Createur: ${context.creatorName}`);
    if (context.audience) ctxLines.push(`Audience: ${context.audience}`);
    if (context.product) ctxLines.push(`Produit/offre: ${context.product}`);
    if (formatName) ctxLines.push(`Format: ${formatName}`);
    const ctxBlock = ctxLines.length > 0 ? `\n=== CONTEXTE ===\n${ctxLines.join('\n')}\n` : '';

    const systemPrompt = `Tu es relecteur final de scripts video face camera en francais. Tu verifies 3 choses:

1. FAITS INVENTES: Le script ne doit contenir AUCUNE stat, chiffre, anecdote, personne ou experience qui n'est pas dans le contexte fourni. Si tu detectes un fait invente, SUPPRIME-LE ou remplace par une formulation vague ("beaucoup de gens" au lieu de "87% des gens").
   ★ EXCEPTION ★ : les **placeholders en gras markdown** (ex: **[ton anecdote]**, **[ton chiffre marquant]**) NE SONT PAS des faits inventés — ce sont des emplacements à remplacer par le créateur. Garde-les INTACTS.

2. INCOHERENCES: Le script doit etre coherent avec le contexte (createur, audience, produit). Un script pour un coach basket qui parle de cuisine = incoherence. Corrige.

3. LANGAGE: Le script doit etre au niveau Flesch-Kincaid Grade 3. Mots de 1-2 syllabes, phrases courtes. Si un mot fait 3+ syllabes ou ne se dit pas dans une conversation entre potes, remplace-le par un mot plus simple. Le reste du texte reste IDENTIQUE.

REGLES:
- Tu ne rajoutes RIEN. Tu ne developpes RIEN. Tu ne reorganises RIEN.
- Le script en sortie a la MEME LONGUEUR (±10%) que l'entree.
- NE TOUCHE PAS au hook (premiere phrase) ni aux labels en MAJUSCULES.
- PRESERVE INTACT tous les marqueurs visuels :
  * Les overlays ecran entre doubles crochets : [[TEXTE A AFFICHER]] — garde-les tels quels (contenu, casse, position)
  * Les didascalies entre parentheses : (montrer son ecran) — telles quelles
  * Les didascalies en italique : *(action visuelle)* — idem
  * Les **placeholders en gras** : **[ton anecdote]**, **[ton chiffre]** — INTOUCHABLES (contenu + asterisques + crochets). Ne les transforme pas en italique, ne les remplace pas par du texte concret, ne les supprime pas.
  * Les numerotations de sections : "Erreur numero 1", "Question #2", "Point 3" — ne pas renommer ni reformuler
- Si rien a corriger, retourne le script EXACTEMENT tel quel.
- Retourne UNIQUEMENT le script corrige. Pas de commentaire.`;
    const cleaned = await askClaude(systemPrompt, [{ role: 'user', content: `${ctxBlock}\n=== SCRIPT A RELIRE ===\n${scriptText}` }], 2500, 30000, CLAUDE_MODEL_SONNET);
    if (!cleaned) return scriptText;
    // Safety: reject if length changed too much
    if (cleaned.length < scriptText.length * 0.75 || cleaned.length > scriptText.length * 1.2) {
      console.warn('[reviewScript] Rejected: length changed too much');
      return scriptText;
    }
    // Remove em dashes and en dashes — they don't exist in spoken French
    return cleaned.trim().replace(/[—–]/g, '-');
  } catch (e) {
    console.warn('[reviewScript] Failed (non-blocking):', e.message);
    return scriptText;
  }
}

// === Hook scorer (fallback) ===
export function scoreHook(userHook, video, insights) {
  if (!userHook || userHook.length < 5) return null
  const words = userHook.toLowerCase().split(/\s+/)
  const checks = []
  let score = 0
  const total = 5

  if (words.length <= 20) { checks.push({ ok: true, text: 'Court et direct' }); score++ }
  else { checks.push({ ok: false, text: 'Trop long  -  max 20 mots' }) }

  const formula = (video.formule_syntaxique || '').toLowerCase()
  if (formula && words.some(w => formula.includes(w))) { checks.push({ ok: true, text: 'Reprend la formule' }); score++ }
  else { checks.push({ ok: false, text: 'Garde la meme structure' }) }

  const painDesire = [insights?.douleurs?.[0]?.val, insights?.desirs?.[0]?.val, insights?.peurs?.[0]?.val].filter(Boolean).join(' ').toLowerCase()
  if (painDesire && words.some(w => w.length > 3 && painDesire.includes(w))) { checks.push({ ok: true, text: 'Touche une douleur/desir' }); score++ }
  else { checks.push({ ok: false, text: 'Nomme une douleur ou desir concret' }) }

  const emotionWords = ['pourquoi', 'comment', 'secret', 'erreur', 'jamais', 'toujours', 'pire', 'meilleur', 'vrai', 'faux', 'personne', 'tout le monde', 'arrete', 'stop']
  if (emotionWords.some(w => userHook.toLowerCase().includes(w)) || userHook.includes('?')) { checks.push({ ok: true, text: 'Mot declencheur present' }); score++ }
  else { checks.push({ ok: false, text: 'Ajoute un mot declencheur' }) }

  const genericPhrases = ['dans cette video', 'salut', 'bonjour', 'hey', "aujourd'hui on va"]
  if (!genericPhrases.some(g => userHook.toLowerCase().includes(g))) { checks.push({ ok: true, text: 'Pas generique' }); score++ }
  else { checks.push({ ok: false, text: 'Evite les intros generiques' }) }

  return { score, total, checks, pct: Math.round(score / total * 100) }
}

// === Stop words for niche matching ===
export const STOP_WORDS = ['les', 'des', 'pour', 'dans', 'avec', 'que', 'qui', 'est', 'son', 'ses', 'mon', 'mes', 'une', 'par', 'sur', 'pas', 'plus', 'tout', 'comme', 'faire', 'fait', 'etre', 'avoir', 'sont', 'cette', 'entre', 'aussi', 'mais', 'bien', 'peut', 'fais', 'veux', 'aide', 'aider', 'gens', 'personnes', 'contenu', 'contenus', 'video', 'videos', 'creer', 'creation', 'coach', 'coaching', 'comment', 'quoi', 'type', 'forme', 'format', 'technique']

// === Script Generation Debug Logging ===
// Logs every generated script to console + localStorage for debugging.
// Does NOT write to generated_scripts (that's the user's saved library).
export function logGeneratedScript({
  userId = null,
  scriptText,
  pipeline,
  sourceVideo = null,
  analysis = null,
  formatType = null,
  hasNonTransposable = false,
  nonTransposableSignals = null,
}) {
  try {
    if (!scriptText) return
    const entry = {
      pipeline,
      generated_at: new Date().toISOString(),
      word_count: scriptText.split(/\s+/).length,
      source_video_titre: sourceVideo?.titre || null,
      source_video_lien: sourceVideo?.lien || null,
      source_video_vues: sourceVideo?.vues || null,
      source_video_format: sourceVideo?.format_nom || formatType || null,
      note_globale: analysis?.note_globale || null,
      script_preview: scriptText.substring(0, 200),
      has_non_transposable: hasNonTransposable || false,
      non_transposable_signals: nonTransposableSignals || null,
    }
    console.log(`[ScriptLog] Pipeline ${pipeline} | ${entry.word_count} mots | ref: ${entry.source_video_titre || '?'} (${entry.source_video_lien || '?'})${hasNonTransposable ? ' | NON-TRANSPOSABLE DETECTED' : ''}`)
    console.log(`[ScriptLog] Script:\n${scriptText}`)

    // Persist last 50 generations in localStorage for debugging
    try {
      const key = 'cg_script_debug_log'
      const existing = JSON.parse(localStorage.getItem(key) || '[]')
      existing.unshift({ ...entry, script_full: scriptText })
      localStorage.setItem(key, JSON.stringify(existing.slice(0, 50)))
    } catch {}
  } catch (e) {
    console.warn('[logGeneratedScript] Error:', e.message)
  }
}
